import { FunctionComponent, useEffect } from "react"
import { SceneWrapper } from "../../../../UI/SceneWrapper"
import { styled, Body1, Button, H2, Link2, useTheme } from "@horizontal-org/shira-ui"
import { ReactComponent as Hooked } from '../../../../../assets/HookedFish.svg';

import { useStore } from "../../../../../store"
import { shallow } from "zustand/shallow"
import { QuizSetupNameScene } from "../../../../../scenes/QuizSetupName"
import { CustomQuiz } from "../CustomQuiz"
import { CustomQuizCompletedScene } from "../../../../../scenes/CustomQuizCompleted"
import { CustomQuizNavbar } from "../../../../UI/CustomQuizNavbar"
import { useTranslation } from "react-i18next"
import { FiChevronRight } from "react-icons/fi"
import { LanguageSelect } from '@horizontal-org/shira-ui'
import { useQuizRun, Answer } from '../../../../../hooks/useQuizRun'
import { getLanguageOptions } from "../../../../../utils/langOptions";

interface Props {
  quiz: any
  learnerQuiz: any
}

export const QuizFlow: FunctionComponent<Props> = ({
  quiz,
  learnerQuiz
}) => {
  console.log("🚀 ~ QuizFlow ~ learnerQuiz:", learnerQuiz)
  const { t, i18n } = useTranslation()
  const theme = useTheme()

  const {
    changeScene,
    scene,
    resetAll,
    updateUserInfo
  } = useStore(
    (state) => ({
      changeScene: state.changeScene,
      scene: state.scene,
      resetAll: state.resetAll,
      updateUserInfo: state.updateName,
    }),
    shallow
  )

  const { finish, recordAnswer, start, started } = useQuizRun(quiz.hasResultsEnabled ?? true);

  useEffect(() => {
    return () => {
      resetAll()
    }
  }, [resetAll])

  const validateQuizStart = () => {
    if (!learnerQuiz) {
      changeScene('quiz-setup-name')
      return
    }

    updateUserInfo(learnerQuiz.learnerName || 'John', learnerQuiz.learnerEmail)
    changeScene('custom-quiz')
  }

  return (
    <>
      {scene === 'welcome' && (
        <SceneWrapper bg='white'>

          <CustomQuizNavbar color={theme.colors.green2} />

          <CenterWrapper>
            <GreenFishWrapper>
              <Hooked />
            </GreenFishWrapper>
            <StyledBox>
              {learnerQuiz ? (<Body1>{learnerQuiz.learnerEmail}</Body1>) : null}
              <Heading>{quiz.title}</Heading>
              <Body1>
                {t('welcome.public_message')}
              </Body1>
              <Buttons>
                <LanguageSelect
                  onChange={(v) => {
                    i18n.changeLanguage(v)
                    localStorage.setItem('lang', v);
                  }}
                  autoselect
                  options={getLanguageOptions(t)}
                />
                <Button
                  text={t('welcome.start')}
                  rightIcon={<FiChevronRight size={18} data-mirror-rtl />}
                  onClick={validateQuizStart}
                />
              </Buttons>
              <LinkWrapper>
                <Link2 href="https://shira.app" target="_blank">
                  {t('welcome.learn_more')}
                </Link2>
              </LinkWrapper>
            </StyledBox>
          </CenterWrapper>
        </SceneWrapper>
      )}

      {scene === 'quiz-setup-name' && (
        <QuizSetupNameScene nextSceneSlug="custom-quiz" />
      )}

      {scene === 'custom-quiz' && (
        <CustomQuiz
          quizId={quiz.id}
          questions={quiz.quizQuestions.map((q) => q.question)}
          quizItems={quiz.quizQuestions}
          images={quiz.images}
          hasResultsEnabled={quiz.hasResultsEnabled ?? true}
          hasAssessmentEnabled={quiz.hasAssessmentEnabled}
          startRun={() => {
            start(quiz.id, learnerQuiz ? learnerQuiz.learnerId : null)
          }}
          recordAnswer={(qId, ans) => recordAnswer(qId, ans as Answer)}
          runStarted={started}
        />
      )}

      {scene === 'completed' && (
        <CustomQuizCompletedScene
          quizNumber={quiz.quizQuestions.filter((item) => item.entityType === 'question').length}
          finish={finish}
          hasAssessmentEnabled={quiz.hasAssessmentEnabled}
        />
      )}
    </>
  )
}

const GreenFishWrapper = styled.div`
  display: flex;
  padding-inline-end: 40px;
  
  > svg {
    width: 410px;
    height: 348px;  
  }

  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    justify-content: flex-end;
    > svg {
      width: 280px;
      height: 275px;
    }
  }

  @media (max-width: ${props => props.theme.breakpoints.xs}) {
    justify-content: space-evenly;
    width: 100%;
    box-sizing: border-box;
    padding: 10px;
  
  > svg {
      width: 230px;
      height: 199px;
    }
  }
`

const Heading = styled(H2)`
  color: ${props => props.theme.colors.green8}
`

const CenterWrapper = styled.div`
  display: flex;
  flex-grow: 1;
  align-items: center;
  justify-content: center;

  @media (max-width: 850px) {
    flex-grow: 0;
    flex-direction: column;
    align-items: center;

    text-align: center;
    padding: 16px;
  }
`

const StyledBox = styled.div`
  display: flex;
  flex-direction: column;
  border: none;
  gap: 24px;
`

const Buttons = styled.div`
  display: flex;
  gap: 16px;

  @media (max-width: ${props => props.theme.breakpoints.xs}) {
    flex-grow: 0;
    flex-direction: column-reverse;
    padding: 0 32px;
  }
`

const LinkWrapper = styled.div`
  display: none;
  
  @media (max-width: ${props => props.theme.breakpoints.xs}) {
    display: flex;
    justify-content: center;
    width: 100%;
    position: absolute;
    bottom: 20px;
    inset-inline-start: 0;
    inset-inline-end: 0;
    margin: 0 auto;
    padding: 0 16px;
    box-sizing: border-box;
    
    > * {
      color: ${props => props.theme.colors.green7};
    }
  }`
